import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  signal,
  resource,
  computed,
  type Signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { Auth } from '@angular/fire/auth';
import { filter, Subject, takeUntil } from 'rxjs';

import { UserService } from '../../services/user.service';
import { environment } from '../../../../environments/environment';
import { SmartOrpcClientService } from '../../services/smart-orpc-client.service';
import { UserRole } from '#backend/common/user-types';
import { ProductAdOverlayComponent } from '../product-ad-overlay/product-ad-overlay.component';
import { urls } from '../../constants';

interface Menu {
  name: string;
  icon?: string;
  iconType?: 'material' | 'svg'; // Tipo do ícone: Material Icon ou SVG customizado
  link?: string;
  action?: () => void; // Ação customizada de clique, para itens que não são rotas do Angular Router
  showChildren?: Signal<boolean>;
  children?: Menu[];
  show?: Signal<boolean>;
  expanded?: boolean;
  disabled?: Signal<boolean>;
  requiredRoles?: UserRole[];
  isUnavailableProduct?: Signal<boolean>; // Menu de produto não contratado
}

@Component({
  selector: 'app-sidebar',
  imports: [
    FormsModule,
    MatSidenavModule,
    MatToolbarModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatDividerModule,
    MatChipsModule,
    MatTooltipModule,
    // CDK Overlay/Portal modules for product ad overlay
    OverlayModule,
    PortalModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  standalone: true,
  animations: [
    trigger('slideWidth', [
      state(
        'collapsed',
        style({
          width: '80px',
        }),
      ),
      state(
        'expanded',
        style({
          width: '320px',
        }),
      ),
      transition(
        'collapsed <=> expanded',
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ),
    ]),
    trigger('fadeSlide', [
      state(
        'hidden',
        style({
          opacity: 0,
          transform: 'translateX(-10px)',
        }),
      ),
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'translateX(0)',
        }),
      ),
      transition(
        'hidden <=> visible',
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ),
    ]),
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate(
          '250ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        animate(
          '250ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0 }),
        ),
      ]),
    ]),
    trigger('rotateIcon', [
      state(
        'collapsed',
        style({
          transform: 'rotate(0deg)',
        }),
      ),
      state(
        'expanded',
        style({
          transform: 'rotate(90deg)',
        }),
      ),
      transition(
        'collapsed <=> expanded',
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ),
    ]),
    trigger('rotateIconMenu', [
      state(
        'collapsed',
        style({
          transform: 'rotate(180deg)',
        }),
      ),
      state(
        'expanded',
        style({
          transform: 'rotate(0deg)',
        }),
      ),
      transition(
        'collapsed <=> expanded',
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ),
    ]),
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  userService = inject(UserService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  userAuthenticated = false;
  orpc = inject(SmartOrpcClientService);
  private overlay = inject(Overlay);
  private auth = inject(Auth);

  pageTitle = signal('');
  isExpanded = signal(true);
  activeLink = signal('');

  // Controle de hover para menu recolhido
  hoveredSection: Menu | null = null;
  hoverTimeout?: NodeJS.Timeout;
  submenuPosition = { top: 0, left: 0 };

  // Expose debug flag to template
  get isDebugMode() {
    return environment.skipAuth;
  }

  currentUserRole = computed(() => {
    return this.userService.user()?.role ?? null;
  });

  isGeoambienteUser = computed(() => {
    const role = this.currentUserRole();
    return (
      role === UserRole.ADMIN_GEOAMBIENTE ||
      role === UserRole.OPERADOR_GEOAMBIENTE
    );
  });

  isAdmin = computed(() => {
    const role = this.currentUserRole();
    return (
      role === UserRole.ADMIN_GEOAMBIENTE || role === UserRole.ADMIN_CLIENTE
    );
  });

  currentContractProducts = resource({
    params: () => ({ organizationId: this.userService.user()?.organizationId }),
    loader: async ({ params }) => {
      const organizationId = params.organizationId;
      if (organizationId == null) return undefined;
      const { data: contract, error } =
        await this.orpc.safeClient.organization.contractSummary({
          organizationId,
        });
      if (error) return undefined;
      return contract.productsIncluded;
    },
  });

  private destroy = new Subject<void>();

  public menu: Menu[] = [
    {
      name: 'AGENTE DATASAFRA (BETA)',
      icon: 'datasafra-agent',
      iconType: 'svg',
      expanded: false,
      link: urls.agent,
    },
    {
      name: 'MONITORAMENTO DA SAFRA',
      isUnavailableProduct: computed(
        () =>
          !this.isDebugMode &&
          !this.isGeoambienteUser() &&
          this.currentContractProducts.value()?.harvestMonitoring !== true,
      ),
      icon: 'monitoring-safra',
      iconType: 'svg',
      expanded: false,
      children: [
        {
          name: 'Solicitação de Monitoramento da Safra',
          link: '/monitoring-safras',
        },
        {
          name: 'Resultados do Monitoramento da Safra',
          link: '/monitoring-safras/results',
        },
      ],
    },
    {
      name: 'HISTÓRICO DE SAFRAS',
      icon: 'historic-safra',
      iconType: 'svg',
      isUnavailableProduct: computed(
        () =>
          !this.isDebugMode &&
          !this.isGeoambienteUser() &&
          this.currentContractProducts.value()?.harvestHistory !== true,
      ),
      expanded: false,
      children: [
        {
          name: 'Solicitação de Histórico de Safras',
          link: '/histories-safras',
        },
        {
          name: 'Relatório de Histórico de Safras',
          link: '/histories-safras/reports',
        },
      ],
    },
    {
      name: 'COMPLIANCE SOCIOAMBIENTAL',
      icon: 'compliance-socioambiental',
      iconType: 'svg',
      isUnavailableProduct: computed(
        () =>
          !this.isDebugMode &&
          !this.isGeoambienteUser() &&
          this.currentContractProducts.value()?.compliance !== true,
      ),
      expanded: false,
      children: [
        {
          name: 'Solicitação de Compliance',
          link: '/compliances',
        },
        {
          name: 'Relatório de Compliance',
          link: '/compliances/reports',
        },
      ],
    },
    {
      name: 'ADMINISTRAÇÃO',
      icon: 'admin_panel_settings',
      show: computed(() => {
        const role = this.currentUserRole();
        if (this.isDebugMode) return true;
        return (
          role === UserRole.ADMIN_CLIENTE ||
          role === UserRole.ADMIN_GEOAMBIENTE ||
          role === UserRole.VISUALIZADOR_CLIENTE // para ver resumo
        );
      }),
      expanded: false,
      children: [
        {
          name: 'Resumo',
          link: urls.customerFacingDashboard,
          show: computed(() => !this.isGeoambienteUser()),
        },
        {
          name: 'Painel de Clientes',
          link: urls.internalDashboard,
          show: computed(() => this.isGeoambienteUser()),
        },
        {
          name: 'Clientes',
          link: '/admin/organizations',
          show: computed(() => this.isGeoambienteUser()),
        },
        {
          name: 'Usuários',
          link: '/admin/users',
          show: computed(() => {
            const role = this.currentUserRole();
            if (this.isDebugMode) return true;
            return (
              role === UserRole.ADMIN_CLIENTE ||
              role === UserRole.ADMIN_GEOAMBIENTE
            );
          }),
        },
        {
          name: 'Contratos',
          link: '/admin/contracts',
          show: computed(() => this.isGeoambienteUser()),
        },
        {
          name: 'ZARC',
          link: '/admin/zarc',
          show: computed(
            () => this.currentUserRole() === UserRole.ADMIN_GEOAMBIENTE,
          ),
        },
      ],
    },
  ];

  ngOnInit(): void {
    this.userService.authentication$
      .pipe(takeUntil(this.destroy))
      .subscribe((isAuthenticated) => {
        this.userAuthenticated = isAuthenticated;

        if (!this.userAuthenticated) this.router.navigate(['login']);
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateHeaderTitle();
      });

    this.updateHeaderTitle();
    this.updateActiveLink();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  onSectionHeaderClick(section: Menu): void {
    // Se é um produto não contratado, abrir propaganda em overlay
    if (section.isUnavailableProduct?.()) {
      const image = this.getAdImageForSection(section);
      if (image) {
        this.showProductAd(image);
        return;
      }
      // fallback: redireciona para dashboard se não encontrar imagem
      this.router.navigate([urls.customerFacingDashboard], {
        replaceUrl: true,
      });
      return;
    }

    if (section.action) {
      section.action();
      return;
    }

    if (section.link) {
      this.router.navigate([section.link], {
        replaceUrl: true,
      });
      return;
    }

    // Caso contrário, comportamento normal (toggle)
    this.toggleSection(section);
  }

  toggleMenu(): void {
    this.isExpanded.update((value) => !value);

    // Quando recolher o menu, recolhe todos os submenus
    if (!this.isExpanded()) {
      this.menu.forEach((section) => {
        section.expanded = false;
      });
    } else {
      // Quando expandir, expandir o submenu da seção ativa
      this.menu.forEach((section) => {
        if (
          section.children?.some((child) => child.link === this.activeLink())
        ) {
          section.expanded = true;
        }
      });
    }
  }

  toggleSection(section: Menu): void {
    if (!this.isExpanded()) {
      // Se o menu está recolhido, não faz nada ao clicar
      return;
    }
    section.expanded = !section.expanded;
  }

  // Hover para menu recolhido
  onSectionMouseEnter(section: Menu, event?: MouseEvent) {
    if (!this.isExpanded()) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = undefined;
      this.hoveredSection = section;

      // Calcular posição do submenu
      if (event) {
        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        this.submenuPosition = {
          top: rect.top,
          left: rect.right + 8,
        };
      }
    }
  }

  onSectionMouseLeave(section: Menu) {
    if (!this.isExpanded()) {
      this.hoverTimeout = setTimeout(() => {
        if (this.hoveredSection === section) {
          this.hoveredSection = null;
        }
      }, 250); // delay para UX
    }
  }

  private overlayRef?: OverlayRef;

  private getAdImageForSection(section: Menu): string | null {
    const name = section.name?.toLowerCase() ?? '';
    if (name.includes('monitoramento'))
      return 'assets/propaganda/03-MONITORAMENTO_DA_SAFRA.png';
    if (name.includes('histórico') || name.includes('historico'))
      return 'assets/propaganda/02-HISTORICO_DE_SAFRAS.png';
    if (name.includes('compliance'))
      return 'assets/propaganda/01-COMPLIANCE_SOCIOAMBIENTAL.png';
    return null;
  }

  private showProductAd(imageUrl: string): void {
    // dispose existing overlay if present
    this.disposeAdOverlay();

    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    const portal = new ComponentPortal(ProductAdOverlayComponent);
    const compRef = this.overlayRef.attach(portal);
    compRef.instance.imageUrl.set(imageUrl);
    compRef.instance.closed.subscribe(() =>
      this.disposeAdOverlay({ reloadProducts: true }),
    );

    this.overlayRef
      .backdropClick()
      .subscribe(() => this.disposeAdOverlay({ reloadProducts: true }));
  }

  private disposeAdOverlay(options?: { reloadProducts: boolean }): void {
    this.overlayRef?.dispose();
    if (options?.reloadProducts) this.currentContractProducts.reload();
  }

  private updateActiveLink(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy),
      )
      .subscribe(() => {
        this.activeLink.set(this.router.url);
      });

    this.activeLink.set(this.router.url);
  }

  private updateHeaderTitle(): void {
    this.getCurrentRoute().title.subscribe({
      next: (title) => {
        this.pageTitle.set(title ?? '');
      },
    });
  }

  handleSignOut(): void {
    this.userService.signOut();
  }

  hasParentRoute() {
    return this.getCurrentRoute().snapshot.url.toString() != '';
  }

  shouldHideBackButton() {
    return (
      this.getCurrentRoute().snapshot.data['HIDE_GLOBAL_BACK_BUTTON'] === true
    );
  }

  up() {
    this.router.navigate(['./'], { relativeTo: this.getParentRoute() });
  }

  goToAgentPage() {
    if (
      this.getCurrentRoute().snapshot.url.toString() !== urls.agent.substring(1)
    )
      this.router.navigate([urls.agent], { replaceUrl: true });
  }

  /**
   * O manual de instruções é servido como app estático separado do SPA (fora do Angular
   * Router), autenticado por cookie de sessão próprio (ver backend/src/server/auth/
   * instructionManual). O cookie é criado sob demanda aqui, só quando o usuário realmente
   * pede pra abrir o manual, em vez de preemptivamente no login — evita manter sessões de
   * 5 dias vivas para quem nunca usa o manual.
   */
  async openInstructionManual(): Promise<void> {
    // Abre a aba de forma síncrona, antes de qualquer await, para não ser bloqueada
    // pelo navegador como pop-up (só é permitido em resposta direta a um clique do usuário).
    const manualTab = window.open('', '_blank');

    const idToken = await this.auth.currentUser?.getIdToken();
    if (!idToken) {
      manualTab?.close();
      return;
    }

    const { error } =
      await this.orpc.safeClient.instructionManual.createSession({ idToken });

    if (error) {
      manualTab?.close();
      return;
    }

    // Precisa ser absoluto (via BACKEND_HOST) e não um path relativo: localmente o frontend
    // (ng serve, porta 4200) e o backend (porta 3000) rodam em origens diferentes, então um
    // path relativo abriria '/manual' dentro do próprio Angular Router.
    const manualUrl = environment.BACKEND_HOST + urls.instructionManual;

    if (manualTab) {
      manualTab.location.href = manualUrl;
    } else {
      // Pop-up bloqueado: navega na aba atual como alternativa.
      window.location.href = manualUrl;
    }
  }

  private getParentRoute() {
    return this.getCurrentRoute().parent;
  }

  private getCurrentRoute() {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    return currentRoute;
  }
}
